/**
 * 🎯 CMT Invitation Modal
 * 
 * Enhanced invitation modal with CMT selection, role assignment,
 * and real-time conflict detection for unique roles.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  Button, 
  Alert, 
  Space, 
  Tag, 
  Typography, 
  Divider,
  Tooltip,
  Spin
} from 'antd';
import { 
  UserAddOutlined, 
  TeamOutlined, 
  SafetyOutlined, 
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import cmtInvitationService, { CMT_ROLE_CONSTRAINTS } from '../../services/cmtInvitationService';
import cmtInvitationTransactions from '../../services/cmtInvitationTransactions';
import RoleConflictModal from './RoleConflictModal';

const { Title, Text } = Typography;
const { Option } = Select;

const CMTInvitationModal = ({ 
  visible, 
  onCancel, 
  onSuccess,
  selectedOrgId,
  selectedOrgName 
}) => {
  const { currentUser } = useAuth();
  const [form] = Form.useForm();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [availableCMTs, setAvailableCMTs] = useState([]);
  const [selectedCMT, setSelectedCMT] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleAvailability, setRoleAvailability] = useState({});
  const [roleConflict, setRoleConflict] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [checkingRoles, setCheckingRoles] = useState(false);
  
  // Conflict resolution modal
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictLoading, setConflictLoading] = useState(false);

  // Load available CMTs when modal opens
  useEffect(() => {
    if (visible && currentUser && selectedOrgId) {
      loadAvailableCMTs();
    }
  }, [visible, currentUser, selectedOrgId]);

  // Check role availability when CMT or role changes
  useEffect(() => {
    if (selectedCMT && selectedRole) {
      checkRoleAvailability();
    } else {
      setRoleConflict(null);
    }
  }, [selectedCMT, selectedRole]);

  const loadAvailableCMTs = async () => {
    try {
      setLoading(true);
      const cmts = await cmtInvitationService.getInvitationEligibleCMTs(
        currentUser.uid, 
        selectedOrgId
      );
      setAvailableCMTs(cmts);
      
      if (cmts.length === 0) {
        message.warning('You do not have permission to invite users to any CMTs in this organization.');
      }
    } catch (error) {
      console.error('Error loading CMTs:', error);
      message.error('Failed to load available CMTs');
    } finally {
      setLoading(false);
    }
  };

  const checkRoleAvailability = async () => {
    try {
      setCheckingRoles(true);
      const conflict = await cmtInvitationService.checkRoleConflict(
        selectedCMT, 
        selectedRole, 
        null, 
        selectedOrgId
      );
      
      setRoleConflict(conflict);
    } catch (error) {
      console.error('Error checking role availability:', error);
      message.error('Failed to check role availability');
    } finally {
      setCheckingRoles(false);
    }
  };

  const handleCMTChange = (cmtId) => {
    setSelectedCMT(cmtId);
    setSelectedRole(null);
    setRoleConflict(null);
    form.setFieldsValue({ cmtRole: undefined });
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
  };

  const handleInvitation = async (formData) => {
    try {
      // Validate permissions first
      const permissionCheck = await cmtInvitationService.canUserInviteToCMT(
        currentUser.uid,
        formData.cmtId,
        selectedOrgId
      );

      if (!permissionCheck.canInvite) {
        message.error(`Cannot invite: ${permissionCheck.reason}`);
        return;
      }

      // Check for conflicts one more time with actual user data
      const conflict = await cmtInvitationService.checkRoleConflict(
        formData.cmtId, 
        formData.cmtRole, 
        null, 
        selectedOrgId
      );
      
      if (conflict.hasConflict) {
        setInvitationData(formData);
        setShowConflictModal(true);
        return;
      }

      // No conflict, proceed with invitation
      await processInvitation(formData);
      
    } catch (error) {
      console.error('Error handling invitation:', error);
      message.error('Failed to process invitation');
    }
  };

  const processInvitation = async (formData, resolutionMethod = null) => {
    try {
      setLoading(true);
      
      const selectedCMTData = availableCMTs.find(cmt => cmt.id === formData.cmtId);
      
      const context = {
        inviterUserId: currentUser.uid,
        orgId: selectedOrgId,
        cmtId: formData.cmtId,
        roleConflict: roleConflict,
        inviterName: currentUser.fullName || currentUser.email,
        cmtName: selectedCMTData?.name || 'CMT',
        orgName: selectedOrgName
      };

      let result;
      if (resolutionMethod && resolutionMethod !== 'cancel') {
        result = await cmtInvitationTransactions.processInvitationWithResolution(
          formData, 
          resolutionMethod, 
          context
        );
      } else if (resolutionMethod === 'cancel') {
        // Don't proceed with invitation
        message.info('Invitation cancelled');
        return;
      } else {
        result = await cmtInvitationTransactions.processSimpleInvitation(
          formData, 
          context
        );
      }
      
      message.success(result.message);
      
      // Reset form and close modal
      form.resetFields();
      setSelectedCMT(null);
      setSelectedRole(null);
      setRoleConflict(null);
      
      if (onSuccess) {
        onSuccess();
      }
      
      onCancel();
      
    } catch (error) {
      console.error('Error processing invitation:', error);
      message.error(`Failed to invite user: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };



  const handleConflictResolution = async (resolutionMethod) => {
    try {
      setConflictLoading(true);
      
      await processInvitation(invitationData, resolutionMethod);
      
      setShowConflictModal(false);
      setInvitationData(null);
      
    } catch (error) {
      console.error('Error resolving conflict:', error);
      message.error('Failed to resolve role conflict');
    } finally {
      setConflictLoading(false);
    }
  };

  const getRoleStatusIcon = (role) => {
    if (checkingRoles) {
      return <LoadingOutlined style={{ color: '#1890ff' }} />;
    }
    
    const constraint = CMT_ROLE_CONSTRAINTS[role];
    if (!constraint?.maxCount) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    
    if (roleConflict?.hasConflict && selectedRole === role) {
      return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  };

  const getRoleStatusText = (role) => {
    if (checkingRoles && selectedRole === role) {
      return 'Checking availability...';
    }
    
    const constraint = CMT_ROLE_CONSTRAINTS[role];
    if (!constraint?.maxCount) {
      return 'Multiple people allowed';
    }
    
    if (roleConflict?.hasConflict && selectedRole === role) {
      const holder = roleConflict.currentHolders?.[0];
      return `Currently held by ${holder?.fullName}`;
    }
    
    return 'Available';
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserAddOutlined style={{ color: '#1890ff' }} />
            <span>Invite User to CMT</span>
          </div>
        }
        open={visible}
        onCancel={onCancel}
        width={600}
        footer={[
          <Button key="cancel" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>,
          <Button 
            key="invite" 
            type="primary" 
            onClick={() => form.submit()}
            loading={loading}
            disabled={!selectedCMT || !selectedRole || checkingRoles}
            icon={roleConflict?.hasConflict ? <ExclamationCircleOutlined /> : <UserAddOutlined />}
          >
            {roleConflict?.hasConflict ? 'Proceed with Role Transfer' : 'Send Invitation'}
          </Button>
        ]}
      >
        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Loading available CMTs...</Text>
            </div>
          </div>
        )}

        {!loading && (
          <>
            {/* Organization Info */}
            <Alert
              type="info"
              message={`Inviting user to: ${selectedOrgName}`}
              style={{ marginBottom: 24 }}
            />

            <Form
              form={form}
              layout="vertical"
              onFinish={handleInvitation}
              autoComplete="off"
            >
              {/* User Details */}
              <Title level={5}>
                <UserAddOutlined /> User Details
              </Title>
              
              <Form.Item
                label="Email Address"
                name="email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input 
                  placeholder="user@example.com"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Full Name"
                name="fullName"
                rules={[{ required: true, message: 'Full name is required' }]}
              >
                <Input 
                  placeholder="John Smith"
                  size="large"
                />
              </Form.Item>

              <Divider />

              {/* CMT Assignment */}
              <Title level={5}>
                <TeamOutlined /> CMT Assignment
              </Title>

              <Form.Item
                label="Assign to CMT"
                name="cmtId"
                rules={[{ required: true, message: 'Please select a CMT' }]}
              >
                <Select
                  placeholder="Select a Crisis Management Team"
                  size="large"
                  onChange={handleCMTChange}
                  loading={loading}
                >
                  {availableCMTs.map(cmt => (
                    <Option key={cmt.id} value={cmt.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{cmt.label}</span>
                        {cmt.userRole && (
                          <Tag color="blue" size="small">
                            You: {cmt.userRole}
                          </Tag>
                        )}
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Role in CMT"
                name="cmtRole"
                rules={[{ required: true, message: 'Please select a role' }]}
              >
                <Select
                  placeholder="Select a role for this user"
                  size="large"
                  onChange={handleRoleChange}
                  disabled={!selectedCMT}
                >
                  {Object.keys(CMT_ROLE_CONSTRAINTS).map(role => (
                    <Option key={role} value={role}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{role}</span>
                        <Space>
                          {CMT_ROLE_CONSTRAINTS[role].maxCount === 1 && (
                            <Tag color="orange" size="small">Unique</Tag>
                          )}
                          {getRoleStatusIcon(role)}
                        </Space>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Role Status Information */}
              {selectedRole && (
                <Alert
                  type={roleConflict?.hasConflict ? 'warning' : 'success'}
                  message={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        <strong>{selectedRole}</strong> - {getRoleStatusText(selectedRole)}
                      </span>
                      {getRoleStatusIcon(selectedRole)}
                    </div>
                  }
                  description={
                    roleConflict?.hasConflict ? (
                      <div>
                        This role is currently occupied. You'll need to resolve the conflict to proceed.
                        <br />
                        <Text type="secondary">
                          The system will guide you through transferring the role safely.
                        </Text>
                      </div>
                    ) : (
                      CMT_ROLE_CONSTRAINTS[selectedRole]?.maxCount === 1 ? 
                        'This is a single-person role, but it\'s currently available.' :
                        'Multiple people can have this role.'
                    )
                  }
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* Permission Info */}
              <Alert
                type="info"
                message="Invitation Process"
                description={
                  <div>
                    <Text>After sending the invitation:</Text>
                    <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                      <li>The user will receive an invitation email</li>
                      <li>They'll also receive a password reset email to set up their account</li>
                      <li>Once they set their password, they can log in with their new role</li>
                    </ul>
                  </div>
                }
                showIcon
              />
            </Form>
          </>
        )}
      </Modal>

      {/* Role Conflict Resolution Modal */}
      <RoleConflictModal
        visible={showConflictModal}
        onCancel={() => {
          setShowConflictModal(false);
          setInvitationData(null);
        }}
        onResolve={handleConflictResolution}
        cmtName={availableCMTs.find(cmt => cmt.id === selectedCMT)?.name}
        targetRole={selectedRole}
        currentHolder={roleConflict?.currentHolders?.[0]}
        newUserEmail={invitationData?.email}
        newUserName={invitationData?.fullName}
        loading={conflictLoading}
      />
    </>
  );
};

export default CMTInvitationModal;
